import type { CSSProperties } from "react";
import { HEX_COLOR_REGEX } from "@openings/api-contract";

export { HEX_COLOR_REGEX };

export const SECTION_WIDTH_MIN = 480;
export const SECTION_WIDTH_MAX = 1600;
export const SECTION_WIDTH_DEFAULT = 1120;
export const SECTION_HEIGHT_MIN = 48;
export const SECTION_HEIGHT_MAX = 960;
export const SECTION_HEIGHT_DEFAULT = 320;
export const FONT_SIZE_MIN = 12;
export const FONT_SIZE_MAX = 36;
export const FONT_SIZE_DEFAULT = 14;
export const FONT_WEIGHT_MIN = 300;
export const FONT_WEIGHT_MAX = 800;
export const FONT_WEIGHT_DEFAULT = 400;

export const TEMPLATE_MAX_WIDTH_VAR =
  "var(--openings-template-max-width, 1120px)";
export const TEMPLATE_FONT_SIZE_VAR =
  "var(--openings-template-font-size, 14px)";
export const TEMPLATE_LINK_FONT_SIZE_VAR =
  "var(--openings-template-link-font-size, 12px)";
export const TEMPLATE_LINK_CLASSNAME = "templateLink";
export const TEMPLATE_ICON_LINK_CLASSNAME = "templateIconLink";

export const TEMPLATE_LINE_HEIGHT_BODY =
  "var(--openings-template-line-height-body, 1.5)";
export const TEMPLATE_LINE_HEIGHT_TIGHT =
  "var(--openings-template-line-height-tight, 1.15)";

/**
 * @deprecated Use `TEMPLATE_COLOR.textSecondary` instead of opacity hacks.
 * Kept temporarily for backward-compat during migration.
 */
export const TEMPLATE_TEXT_MUTED_OPACITY = 0.75;

/* ── Color tokens ── */

export const TEMPLATE_COLOR = {
  text: "var(--openings-color-text, #111)",
  textSecondary: "var(--openings-color-text-secondary, rgba(0, 0, 0, 0.45))",
  surface: "var(--openings-color-surface, #ffffff)",
  surfaceAlt: "var(--openings-color-surface-alt, #fafafa)",
  background: "var(--openings-color-background, #ffffff)",
  border: "var(--openings-color-border, #d9d9d9)",
  primary: "var(--openings-color-primary, #111)",
  primaryText: "var(--openings-color-primary-text, #fff)",
} as const;

/* ── Border-radius tokens ── */

export const TEMPLATE_RADIUS = {
  /** Cards, panels, inputs (10px) */
  md: "var(--openings-template-radius, 10px)",
  /** Buttons, thumbnails, badges (6px) */
  sm: "var(--openings-template-radius-sm, 6px)",
  /** Service cards, larger UI surfaces (16px) */
  lg: "var(--openings-template-radius-lg, 16px)",
  /** Pills, full-round buttons */
  full: "999px",
} as const;

/* ── Shadow tokens ── */

export const TEMPLATE_SHADOW = {
  sm: "0 1px 2px rgba(0,0,0,0.05)",
  md: "0 2px 8px rgba(0,0,0,0.08)",
} as const;

/* ── Border shorthand ── */

export const TEMPLATE_BORDER = `1px solid ${TEMPLATE_COLOR.border}` as const;

export const TEMPLATE_SPACING = {
  xs: "var(--openings-template-space-xs, 8px)",
  sm: "var(--openings-template-space-sm, 12px)",
  md: "var(--openings-template-space-md, 16px)",
  lg: "var(--openings-template-space-lg, 24px)",
  xl: "var(--openings-template-space-xl, 32px)",
  xxl: "var(--openings-template-space-xxl, 48px)",
} as const;

export const TEMPLATE_SECTION_PADDING_Y = TEMPLATE_SPACING.sm;
export const TEMPLATE_SECTION_PADDING_X = TEMPLATE_SPACING.sm;

export const TEMPLATE_TYPE_SCALE = {
  eyebrow: "var(--openings-template-type-eyebrow, 0.75em)",
  h1: "var(--openings-template-type-h1, 3em)",
  h2: "var(--openings-template-type-h2, 1.75em)",
  h3: "var(--openings-template-type-h3, 1.5em)",
  h4: "var(--openings-template-type-h4, 1.125em)",
  body: "var(--openings-template-type-body, 1em)",
  small: "var(--openings-template-type-small, 0.875em)",
  micro: "var(--openings-template-type-micro, 0.75em)",
  logo: "var(--openings-template-type-logo, 1.25em)",
  logoCompact: "var(--openings-template-type-logo-compact, 1.125em)",
} as const;

export function clampNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.min(Math.max(value, min), max);
  }
  return fallback;
}

export function resolveColor(value: unknown, fallback: string): string {
  if (typeof value === "string" && HEX_COLOR_REGEX.test(value)) {
    return value;
  }
  return fallback;
}

export function resolveMaxWidth(value: unknown): number | null {
  if (value === null) return null;
  return clampNumber(
    value,
    SECTION_WIDTH_MIN,
    SECTION_WIDTH_MAX,
    SECTION_WIDTH_DEFAULT,
  );
}

export function resolveMinHeight(value: unknown): number | null {
  if (value === null) return null;
  return clampNumber(
    value,
    SECTION_HEIGHT_MIN,
    SECTION_HEIGHT_MAX,
    SECTION_HEIGHT_DEFAULT,
  );
}

export function resolveFontSize(value: unknown): number {
  return clampNumber(value, FONT_SIZE_MIN, FONT_SIZE_MAX, FONT_SIZE_DEFAULT);
}

export function resolveFontWeight(value: unknown): number {
  return clampNumber(
    value,
    FONT_WEIGHT_MIN,
    FONT_WEIGHT_MAX,
    FONT_WEIGHT_DEFAULT,
  );
}

export function resolveTextAlign(value: unknown): CSSProperties["textAlign"] {
  if (value === "left" || value === "center" || value === "right") {
    return value;
  }
  return "left";
}

export function resolveBackgroundImageUrl(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  return null;
}
